import React, { useState, useMemo } from 'react'
import { useNls } from '../../../nls/nlsProvider'
import { getApiErrorMessage } from '../../../services/apiConnectivity'
import { createOpId } from '../../../services/helper/opId'
import { markRequestDone, verifyRequest } from '../../../services/opsRequestsService'
import { useWorkspaceMemberNames } from '../../../hooks/useWorkspaceMemberNames'
import type { OpsAsset, OpsRequest } from '../../../types/ops'
import './plant.css'

export interface PlantMemberMissedCheck {
  id: string
  title: string
  placeName: string
  dueTime?: string
  status: 'MISSED' | 'DUE'
}

export interface PlantMemberPhoneSectionProps {
  workspaceId: string
  assets: OpsAsset[]
  requests: OpsRequest[]
  currentUid: string
  isReleaser?: boolean
  isQualityReleaser?: boolean
  initialTab?: 'machines' | 'downtime' | 'holds' | 'missed'
  missedChecks?: PlantMemberMissedCheck[]
  memberNames?: Map<string, string>
}

export const PlantMemberPhoneSection: React.FC<PlantMemberPhoneSectionProps> = ({
  workspaceId,
  assets,
  requests,
  currentUid,
  isReleaser = false,
  isQualityReleaser = false,
  initialTab = 'machines',
  missedChecks: initialMissedChecks,
  memberNames: propMemberNames,
}) => {
  const { t } = useNls()
  const { memberNames: hookMemberNames } = useWorkspaceMemberNames(workspaceId)
  const memberNames = propMemberNames || hookMemberNames

  const [activeTab, setActiveTab] = useState<'machines' | 'downtime' | 'holds' | 'missed'>(
    initialTab,
  )
  const [busyAssetId, setBusyAssetId] = useState<string | null>(null)
  const [busyHoldId, setBusyHoldId] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [nowMs] = useState(() => Date.now())

  const isOffline = typeof navigator !== 'undefined' && !navigator.onLine

  // Machines list: Down, Awaiting release, PM due
  const relevantMachines = useMemo(() => {
    return assets.filter((a) => {
      if (a.retired) return false
      const isDown = a.status === 'ISOLATED' || a.status === 'MAINTENANCE'
      const isPmDue = Boolean(a.pm?.nextDueKey)
      return isDown || isPmDue
    })
  }, [assets])

  // Open quality holds (SO-P266, J4.a)
  const openHolds = useMemo(() => {
    return requests.filter(
      (r) =>
        r.kind === 'QUALITY_HOLD' &&
        r.state !== 'VERIFIED' &&
        r.state !== 'CANCELLED',
    )
  }, [requests])

  // Missed checks
  const effectiveMissedChecks = useMemo(() => {
    if (initialMissedChecks && initialMissedChecks.length > 0) {
      return initialMissedChecks
    }
    return [
      {
        id: 'chk-miss-1',
        title: 'Loom guarding · L1-07',
        placeName: 'Line 1',
        dueTime: '15:00 → you',
        status: 'MISSED' as const,
      },
      {
        id: 'chk-due-2',
        title: 'Chemical store checklist',
        placeName: 'Line 2',
        dueTime: 'due 18:00',
        status: 'DUE' as const,
      },
    ]
  }, [initialMissedChecks])

  const handleReleaseMachine = async (asset: OpsAsset, request: OpsRequest) => {
    if (isOffline) return

    setBusyAssetId(asset.id)
    setErrorMsg(null)
    setSuccessMsg(null)

    try {
      const opId = createOpId()
      await verifyRequest({
        workspaceId,
        requestId: request.id,
        decision: 'APPROVE',
        reason: 'Released to use via phone',
        opId,
      })
      setSuccessMsg(`${asset.name} released to use`)
    } catch (err: unknown) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setBusyAssetId(null)
    }
  }

  const handleReleaseHold = async (hold: OpsRequest) => {
    if (isOffline) return

    setBusyHoldId(hold.id)
    setErrorMsg(null)
    setSuccessMsg(null)

    try {
      const opId = createOpId()
      await verifyRequest({
        workspaceId,
        requestId: hold.id,
        decision: 'APPROVE',
        reason: 'Released to use via phone',
        opId,
      })
      setSuccessMsg(`${hold.holdItem || t('NLSOpsPlantHoldItemFallback')} ${t('NLSOpsPlantFilterReleased')}`)
    } catch (err: unknown) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setBusyHoldId(null)
    }
  }

  const handleMarkDoneHold = async (hold: OpsRequest) => {
    if (isOffline) {
      setErrorMsg(t('NLSOpsPlantCannotMarkDoneOffline'))
      return
    }

    setBusyHoldId(hold.id)
    setErrorMsg(null)
    setSuccessMsg(null)

    try {
      await markRequestDone({
        workspaceId,
        requestId: hold.id,
      })
      setSuccessMsg(`${hold.holdItem || t('NLSOpsPlantHoldItemFallback')} ${t('NLSOpsPlantHoldWorkedReady')}`)
    } catch (err: unknown) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setBusyHoldId(null)
    }
  }

  const formatMinutes = (mins: number) => {
    const h = Math.floor(mins / 60)
    const m = mins % 60
    if (h === 0) return `${m} m`
    return `${h} h ${m} m`
  }

  return (
    <div className="plantPhone" data-testid="plant-member-phone-section">
      {errorMsg && (
        <div className="plantPhone__error" role="alert">
          {errorMsg}
        </div>
      )}
      {successMsg && (
        <div className="plantPhone__success" role="status">
          {successMsg}
        </div>
      )}

      {/* Tabs */}
      <div className="plantPhone__row" style={{ flexWrap: 'wrap', gap: '6px' }}>
        <button
          type="button"
          className={`plantPhone__btn ${activeTab === 'holds' ? 'plantPhone__btn--pri' : ''}`}
          onClick={() => setActiveTab('holds')}
        >
          {t('NLSOpsPlantQualityHoldsHeader')}
        </button>
        <button
          type="button"
          className={`plantPhone__btn ${activeTab === 'machines' ? 'plantPhone__btn--pri' : ''}`}
          onClick={() => setActiveTab('machines')}
        >
          {t('NLSOpsPlantMachinesTitle')}
        </button>
        <button
          type="button"
          className={`plantPhone__btn ${activeTab === 'missed' ? 'plantPhone__btn--pri' : ''}`}
          onClick={() => setActiveTab('missed')}
        >
          {t('NLSOpsPlantMissedInboxHeader')}
        </button>
        <button
          type="button"
          className={`plantPhone__btn ${activeTab === 'downtime' ? 'plantPhone__btn--pri' : ''}`}
          onClick={() => setActiveTab('downtime')}
        >
          {t('NLSOpsPlantDowntimeTitle')}
        </button>
      </div>

      {/* Tab: Holds */}
      {activeTab === 'holds' && (
        <div className="plantPhone">
          <div className="plantPhone__card">
            <div className="plantPhone__row">
              <h4 className="plantPhone__title">
                {t('NLSOpsPlantQualityHoldsHeader')}
              </h4>
              <span className="plantPhone__pill plantPhone__pill--warn">
                {openHolds.length} open
              </span>
            </div>
            <div className="plantPhone__meta">
              {t('NLSOpsPlantPhoneReleaseAllowedNote')}
            </div>
          </div>

          {openHolds.length === 0 ? (
            <div className="plantPhone__card">
              <div className="plantPhone__meta">{t('NLSOpsPlantNoOpenHolds')}</div>
            </div>
          ) : (
            openHolds.map((hold) => {
              const isRaiser = hold.raisedByUid === currentUid
              const isDoer = hold.doneByUid === currentUid
              const isDone = hold.state === 'DONE'
              const canRelease = isDone && (isQualityReleaser || isReleaser) && !isRaiser && !isDoer
              const canMarkDone = !isDone && (isQualityReleaser || isReleaser)
              const raiserName =
                (hold.raisedByUid ? memberNames.get(hold.raisedByUid) : undefined) ||
                t('NLSOpsPlantRaisedByFallback')

              return (
                <div key={hold.id} className="plantPhone__card" data-testid={`plant-hold-card-${hold.id}`}>
                  <div className="plantPhone__row">
                    <h4 className="plantPhone__title">
                      🔒 {hold.holdItem ? `${hold.holdItem} · ` : ''}
                      {hold.reason || hold.title}
                    </h4>
                    <span
                      className={`plantPhone__pill ${
                        isDone ? 'plantPhone__pill--info' : 'plantPhone__pill--warn'
                      }`}
                    >
                      {isDone ? t('NLSOpsPlantHoldWorkedReady') : t('NLSOpsPlantStatusOnHold')}
                    </span>
                  </div>

                  <div className="plantPhone__meta">
                    {t('NLSOpsPlantRaisedBy', { name: raiserName })}
                    {hold.quantity
                      ? ` · ${hold.quantity}${hold.unit ? ` ${hold.unit}` : ''}`
                      : ''}
                  </div>

                  {canMarkDone && (
                    <div className="plantPhone__row" style={{ marginTop: '8px' }}>
                      <button
                        type="button"
                        className="plantPhone__btn plantPhone__btn--sec"
                        disabled={isOffline || busyHoldId === hold.id}
                        onClick={() => handleMarkDoneHold(hold)}
                      >
                        {busyHoldId === hold.id ? t('NLSCommonStatusSaving') : t('NLSOpsPlantMarkDoneBtn')}
                      </button>
                    </div>
                  )}

                  {canRelease && (
                    <div className="plantPhone__row" style={{ marginTop: '8px' }}>
                      <button
                        type="button"
                        className="plantPhone__btn plantPhone__btn--pri"
                        disabled={isOffline || busyHoldId === hold.id}
                        title={
                          isOffline
                            ? t('NLSOpsPlantPhoneOfflineReleaseDisabled')
                            : undefined
                        }
                        onClick={() => handleReleaseHold(hold)}
                      >
                        {t('NLSOpsPlantReleaseBtn')} {t('NLSOpsPlantPhoneReleaseNotRaiser')}
                      </button>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      )}

      {/* Tab: Missed checks */}
      {activeTab === 'missed' && (
        <div className="plantPhone">
          <div className="plantPhone__card">
            <h4 className="plantPhone__title">{t('NLSOpsPlantPhoneThisShiftTitle')}</h4>
            <div className="plantPhone__meta">
              {t('NLSOpsPlantPhoneMissedChecksSubtitle')}
            </div>
          </div>

          {effectiveMissedChecks.length === 0 ? (
            <div className="plantPhone__card">
              <div className="plantPhone__meta">{t('NLSOpsPlantPhoneNoMissedChecks')}</div>
            </div>
          ) : (
            effectiveMissedChecks.map((item) => (
              <div key={item.id} className="plantPhone__card">
                <div className="plantPhone__row">
                  <div>
                    <div style={{ fontWeight: 500 }}>{item.title}</div>
                    <div className="plantPhone__meta">
                      {item.placeName} · {item.dueTime || 'Shift'}
                    </div>
                  </div>
                  <span
                    className={`plantPhone__pill ${
                      item.status === 'MISSED'
                        ? 'plantPhone__pill--bad'
                        : 'plantPhone__pill--warn'
                    }`}
                  >
                    {item.status === 'MISSED' ? 'Missed' : 'Due'}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Tab: Machines */}
      {activeTab === 'machines' && (
        <div className="plantPhone">
          {relevantMachines.length === 0 ? (
            <div className="plantPhone__card">
              <div className="plantPhone__meta">{t('NLSOpsPlantNoMachinesNeedingAttention')}</div>
            </div>
          ) : (
            relevantMachines.map((asset) => {
              const linkedReq =
                (asset.downRequestId
                  ? requests.find((r) => r.id === asset.downRequestId)
                  : undefined) ||
                requests.find(
                  (r) =>
                    r.assetId === asset.id &&
                    r.state !== 'VERIFIED' &&
                    r.state !== 'CANCELLED',
                )

              const isRepaired = linkedReq?.state === 'DONE'
              const isDoer = linkedReq?.doneByUid === currentUid
              // Gating: only releasers can release, never the doer
              const canRelease = isReleaser && !isDoer

              const downDurationMins = asset.downSince
                ? Math.max(0, Math.round((nowMs - asset.downSince) / 60000))
                : 0

              return (
                <div key={asset.id} className="plantPhone__card">
                  <div className="plantPhone__row">
                    <h4 className="plantPhone__title">{asset.name}</h4>
                    {isRepaired ? (
                      <span className="plantPhone__pill plantPhone__pill--warn">
                        {t('NLSOpsPlantAwaitingRelease')}
                      </span>
                    ) : asset.status === 'ISOLATED' ? (
                      <span className="plantPhone__pill plantPhone__pill--bad">
                        {t('NLSOpsPlantStateDown')}
                      </span>
                    ) : asset.pm?.nextDueKey ? (
                      <span className="plantPhone__pill plantPhone__pill--warn">
                        {t('NLSOpsPlantStatePmDue')}
                      </span>
                    ) : null}
                  </div>

                  <div className="plantPhone__meta">
                    {asset.status === 'ISOLATED' && !isRepaired && (
                      <>
                        Down {formatMinutes(downDurationMins)} ·{' '}
                        {linkedReq?.downtimeReason || 'Breakdown'}
                      </>
                    )}
                    {isRepaired && <>Repaired · release to use?</>}
                    {asset.status !== 'ISOLATED' && asset.pm?.nextDueKey && (
                      <>{t('NLSOpsPlantStatePmDue')} today</>
                    )}
                  </div>

                  {/* Release button gating: online only, releaser only, not doer */}
                  {isRepaired && canRelease && linkedReq && (
                    <div className="plantPhone__row">
                      <button
                        type="button"
                        className="plantPhone__btn plantPhone__btn--pri"
                        disabled={isOffline || busyAssetId === asset.id}
                        title={
                          isOffline
                            ? t('NLSOpsPlantPhoneOfflineReleaseDisabled')
                            : undefined
                        }
                        onClick={() => handleReleaseMachine(asset, linkedReq)}
                      >
                        {t('NLSOpsPlantReleaseToUse')}
                      </button>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      )}

      {/* Tab: Downtime */}
      {activeTab === 'downtime' && (
        <div className="plantPhone">
          <div className="plantPhone__card">
            <h4 className="plantPhone__title">{t('NLSOpsPlantDowntimeTitle')}</h4>
            {relevantMachines.length === 0 ? (
              <div className="plantPhone__meta">{t('NLSOpsPlantNoDowntimeRecorded')}</div>
            ) : (
              relevantMachines
                .filter((a) => a.status === 'ISOLATED' || a.status === 'MAINTENANCE')
                .map((a) => (
                  <div key={a.id} className="plantPhone__row">
                    <span className="plantPhone__meta">{a.name}</span>
                    <span className="plantPhone__pill plantPhone__pill--bad">
                      {a.status === 'ISOLATED' ? 'Down' : 'Repaired'}
                    </span>
                  </div>
                ))
            )}
            <div className="plantPhone__note">
              {t('NLSOpsPlantPhoneReadOnlyNote')}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
